export const LEAD_SENT_STORAGE_PREFIX = 'arkorbit-lead-sent-at';
/** @deprecated Legacy global key — removed on read when per-email keys are used */
export const LEAD_SENT_STORAGE_KEY = LEAD_SENT_STORAGE_PREFIX;
export const LEAD_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export interface LeadSubmitGuardState {
  canSubmit: boolean;
  message?: string;
  retryAfterMs?: number;
}

export function normalizeLeadEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function leadSentStorageKey(email: string): string {
  return `${LEAD_SENT_STORAGE_PREFIX}:${normalizeLeadEmail(email)}`;
}

function readCooldownState(
  storageKey: string,
  now: number,
): LeadSubmitGuardState | null {
  const raw = localStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }

  const sentAt = Number.parseInt(raw, 10);
  if (!Number.isFinite(sentAt)) {
    localStorage.removeItem(storageKey);
    return null;
  }

  const elapsed = now - sentAt;
  if (elapsed >= LEAD_COOLDOWN_MS) {
    localStorage.removeItem(storageKey);
    return null;
  }

  const hoursLeft = Math.max(1, Math.ceil((LEAD_COOLDOWN_MS - elapsed) / (60 * 60 * 1000)));
  return {
    canSubmit: false,
    retryAfterMs: LEAD_COOLDOWN_MS - elapsed,
    message: `Anfrage bereits gesendet. Wir melden uns innerhalb von 48 Stunden — erneutes Senden ist in ca. ${hoursLeft} Std. möglich.`,
  };
}

export function getLeadSubmitGuard(email: string, now = Date.now()): LeadSubmitGuardState {
  if (typeof window === 'undefined') {
    return { canSubmit: true };
  }

  const normalizedEmail = normalizeLeadEmail(email);
  if (!normalizedEmail) {
    return { canSubmit: true };
  }

  // Drop legacy browser-global cooldown so other addresses are not blocked.
  localStorage.removeItem(LEAD_SENT_STORAGE_PREFIX);

  const blocked = readCooldownState(leadSentStorageKey(normalizedEmail), now);
  return blocked ?? { canSubmit: true };
}

export function markLeadSubmitted(email: string, now = Date.now()): void {
  if (typeof window === 'undefined') return;

  const normalizedEmail = normalizeLeadEmail(email);
  if (!normalizedEmail) return;

  localStorage.removeItem(LEAD_SENT_STORAGE_PREFIX);
  localStorage.setItem(leadSentStorageKey(normalizedEmail), String(now));
}

export function createLeadIdempotencyKey(email: string, now = Date.now()): string {
  const day = new Date(now).toISOString().slice(0, 10);
  return `${normalizeLeadEmail(email)}:${day}`;
}
